import { baseApi } from '@/features/api/baseApi';
import { type RequestQuery } from '@/features/api/directus';
import {
  type ReadOneQueryArg,
  buildCountFn,
  buildCreateFn,
  buildDeleteFn,
  buildDeleteManyFn,
  buildReadManyFn,
  buildReadOneFn,
  buildUpdateFn,
  buildUpdateManyFn,
} from '@/features/api/endpoints/createDirectusEndpoints';
import type { Etablissement } from '@/models/types';

const etablissementApi = baseApi
  .enhanceEndpoints({
    addTagTypes: ['etablissement', 'etablissements'],
  })
  .injectEndpoints({
    endpoints: builder => ({
      getEtablissements: builder.query<Etablissement[], RequestQuery<Etablissement>>({
        queryFn: buildReadManyFn<Etablissement>('Etablissement'),
        providesTags: ['etablissements'],
      }),
      getEtablissement: builder.query<Etablissement, ReadOneQueryArg<Etablissement, string>>({
        queryFn: buildReadOneFn<Etablissement, string>('Etablissement'),
        providesTags: ['etablissement'],
      }),
      getEtablissementCount: builder.query<number, RequestQuery<Etablissement> | undefined>({
        queryFn: buildCountFn<Etablissement>('Etablissement'),
        providesTags: ['etablissements'],
      }),
      createEtablissement: builder.mutation<Etablissement, Partial<Etablissement>>({
        queryFn: buildCreateFn<Etablissement>('Etablissement'),
        invalidatesTags: ['etablissement', 'etablissements'],
      }),
      updateEtablissement: builder.mutation<
        Etablissement,
        { id: string; body: Partial<Etablissement> }
      >({
        queryFn: buildUpdateFn<Etablissement, string>('Etablissement'),
        invalidatesTags: ['etablissement', 'etablissements'],
      }),
      updateEtablissements: builder.mutation<
        Etablissement[],
        { ids: string[]; body: Partial<Etablissement> }
      >({
        queryFn: buildUpdateManyFn<Etablissement, string>('Etablissement'),
        invalidatesTags: ['etablissement', 'etablissements'],
      }),
      deleteEtablissement: builder.mutation<void, string>({
        queryFn: buildDeleteFn<string>('Etablissement'),
        invalidatesTags: ['etablissement', 'etablissements'],
      }),
      deleteEtablissements: builder.mutation<void, string[]>({
        queryFn: buildDeleteManyFn<string>('Etablissement'),
        invalidatesTags: ['etablissement', 'etablissements'],
      }),
    }),
  });

export const {
  useGetEtablissementsQuery,
  useGetEtablissementQuery,
  useGetEtablissementCountQuery,
  useCreateEtablissementMutation,
  useUpdateEtablissementMutation,
  useUpdateEtablissementsMutation,
  useDeleteEtablissementMutation,
  useDeleteEtablissementsMutation,
} = etablissementApi;
